/**
 * contentEditable 欄位的輸入行為修正（貼文 / 世界觀的標題與內文共用）。
 *
 * 讓瀏覽器原生貼上會把來源的 HTML 格式一起帶進來，因此一律攔下改插純文字。
 *
 * 插入必須走 execCommand('insertText')：它屬於使用者代理發起的編輯動作，會照常送出
 * input 事件、也保留原生 undo stack。若改用 Range.insertNode() 手動塞節點，畫面會變、
 * 但 input 事件不會發，掛在 onInput 的變更偵測完全收不到
 * （症狀：編輯貼文貼上內文後，儲存鍵仍維持 disabled）。
 */

const insertText = (event, text) => {
  event.preventDefault();
  document.execCommand('insertText', false, text);
};

/**
 * 多行欄位（貼文 / 世界觀內文）的貼上：只剝格式，保留換行。
 *
 * @param {React.ClipboardEvent} event
 */
export const pastePlainText = (event) => {
  insertText(event, event.clipboardData.getData('text/plain'));
};

// 只壓「真正的斷行」。不能用 \s+：那會連 U+3000 表意空格（日文標題常用的全形空格）
// 一起換成半形，等於靜默改寫使用者的內容，超出單行化的目的。
// 除了 CR / LF，UAX#14 列為強制斷行、瀏覽器也會照斷的 VT / FF / NEL / LS / PS 一併列入。
// 注意：帶 g 旗標的正則有 lastIndex 狀態，這個常數只給 String.replace 用（replace 會自行
// 重置 lastIndex）；不要拿去 .test() / .exec()，那會在連續呼叫間帶狀態
const LINE_BREAKS = /[\r\n\v\f\u0085\u2028\u2029]+/g;

/**
 * 把斷行壓成單一空格，其餘空白（含 U+3000 全形空格）原樣保留。
 *
 * 單行欄位在「貼上」與「送出」兩端都要用：貼上擋掉最大宗的來源，送出則收掉
 * 拖曳文字、行動裝置軟鍵盤的換行鍵、語音聽寫的換行指令，以及 DB 既有的髒資料 ——
 * 這些路徑都不會經過 onPaste / onKeyDown，但一定會經過送出。
 *
 * @param {string} text
 * @returns {string}
 */
export const toSingleLine = (text) => text.replace(LINE_BREAKS, ' ');

/**
 * 單行欄位（世界觀標題）的貼上：斷行壓成單一空格，其餘空白原樣保留。
 * 標題是 contentEditable 而非 <input>，不壓的話貼上多行文字會直接產生 <br>。
 *
 * @param {React.ClipboardEvent} event
 */
export const pastePlainTextSingleLine = (event) => {
  insertText(event, toSingleLine(event.clipboardData.getData('text/plain')));
};

/**
 * 單行欄位擋掉 Enter 換行，行為比照 <input>。
 * 與 pastePlainTextSingleLine 成對使用，兩個插入換行的入口才都堵住。
 *
 * 輸入法組字期間的 Enter 是「確定候選字」而非換行，擋掉會讓中日文無法輸入。
 * 多數瀏覽器組字中的 keydown 會回報 key='Process'，但 Safari 與部分 Android IME
 * 仍回報 'Enter'，因此一律以 isComposing 為準；React 的 SyntheticKeyboardEvent
 * 沒有這個欄位（見 KeyboardEventInterface），必須走 nativeEvent。
 *
 * @param {React.KeyboardEvent} event
 */
export const blockNewline = (event) => {
  if (event.key === 'Enter' && !event.nativeEvent.isComposing) event.preventDefault();
};
